import { forwardRef } from 'react'

import styles from './CheckoutHeader.module.css'
import { LanguageSwitcher } from '../../components/LanguageSwitcher'

import type { SecurityBadge } from '../../types'

export interface CheckoutHeaderProps {
  /** Logo image URL */
  logo: string
  /** Brand name for alt text */
  brandName: string
  /** Logo width in pixels */
  logoWidth?: number
  /** Logo height in pixels */
  logoHeight?: number
  /** Security badges to display */
  securityBadges?: SecurityBadge[]
  /** Whether to show the language switcher */
  showLanguageSwitcher?: boolean
  /** Language switcher size */
  languageSwitcherSize?: 'sm' | 'md'
  /** Additional class name */
  className?: string
  /** Callback when logo fails to load */
  onLogoError?: () => void
}

/**
 * CheckoutHeader - Header component for checkout page
 *
 * Displays logo, security badges, and language switcher
 */
export const CheckoutHeader = forwardRef<HTMLDivElement, CheckoutHeaderProps>(
  (
    {
      logo,
      brandName,
      logoWidth,
      logoHeight = 40,
      securityBadges = [],
      showLanguageSwitcher = true,
      languageSwitcherSize = 'sm',
      className,
      onLogoError,
    },
    ref
  ) => {
    return (
      <div ref={ref} className={`${styles.header} ${className ?? ''}`}>
        {/* Left: Logo */}
        <div className={styles.left} data-ga-module="checkout_header">
          <img
            src={logo}
            alt={brandName}
            width={logoWidth}
            height={logoHeight}
            className={styles.logo}
            onError={onLogoError}
          />
        </div>

        {/* Center: Security badges */}
        <div className={styles.center}>
          {securityBadges.length > 0 && (
            <div className={styles.badges}>
              {securityBadges.map((badge, index) => (
                <img
                  key={index}
                  src={badge.src}
                  alt={badge.alt}
                  width={badge.width}
                  height={badge.height ?? 32}
                  className={styles.badge}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right: Language switcher */}
        <div className={styles.right}>
          {showLanguageSwitcher && <LanguageSwitcher size={languageSwitcherSize} />}
        </div>
      </div>
    )
  }
)

CheckoutHeader.displayName = 'CheckoutHeader'
